import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { useForm, Controller } from 'react-hook-form';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  IconButton,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  Paper,
} from '@mui/material';
import {
  DeleteRounded as DeleteIcon,
  PushPinRounded as PinIcon,
  SendRounded as SendIcon,
} from '@mui/icons-material';
import { GET_MESSAGE_BOARD_POSTS } from '../apollo/queries';
import {
  CREATE_MESSAGE_BOARD_POST,
  DELETE_MESSAGE_BOARD_POST,
  UPDATE_MESSAGE_BOARD_POST,
} from '../apollo/mutations';
import { formatDateTime } from '../hooks/usePersonHelpers';

function MessageBoardPage() {
  const [error, setError] = useState('');
  const { control, handleSubmit, reset, formState: { errors } } = useForm({
    defaultValues: { authorName: '', body: '' },
  });

  const { data, loading, error: queryError } = useQuery(GET_MESSAGE_BOARD_POSTS);

  const [createPost, { loading: creating }] = useMutation(CREATE_MESSAGE_BOARD_POST, {
    refetchQueries: [{ query: GET_MESSAGE_BOARD_POSTS }],
    onCompleted: () => {
      reset({ authorName: '', body: '' });
      setError('');
    },
    onError: (err) => setError(err.message),
  });

  const [deletePost] = useMutation(DELETE_MESSAGE_BOARD_POST, {
    refetchQueries: [{ query: GET_MESSAGE_BOARD_POSTS }],
    onError: (err) => setError(err.message),
  });

  const [updatePost] = useMutation(UPDATE_MESSAGE_BOARD_POST, {
    refetchQueries: [{ query: GET_MESSAGE_BOARD_POSTS }],
    onError: (err) => setError(err.message),
  });

  const onSubmit = (data) => {
    if (!data.authorName.trim() || !data.body.trim()) {
      setError('Please fill in all fields');
      return;
    }
    createPost({
      variables: {
        authorName: data.authorName.trim(),
        body: data.body.trim(),
      },
    });
  };

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this post?')) {
      deletePost({ variables: { id } });
    }
  };

  const handleTogglePin = (post) => {
    updatePost({ variables: { id: post.id, pinned: !post.pinned } });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (queryError) {
    return <Alert severity="error">Error loading posts: {queryError.message}</Alert>;
  }

  const posts = data?.messageBoardPosts || [];

  return (
    <Box>
      <Typography variant="h4" gutterBottom fontWeight={600}>
        💬 Message Board
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Share announcements, updates, and messages with the family
      </Typography>

      {/* New Post Form */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Post a Message
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        <form onSubmit={handleSubmit(onSubmit)}>
          <Controller
            name="authorName"
            control={control}
            rules={{ required: 'Name is required' }}
            render={({ field }) => (
              <TextField
                {...field}
                label="Your Name"
                fullWidth
                size="small"
                sx={{ mb: 2 }}
                error={!!errors.authorName}
                helperText={errors.authorName?.message}
              />
            )}
          />
          <Controller
            name="body"
            control={control}
            rules={{ required: 'Message is required' }}
            render={({ field }) => (
              <TextField
                {...field}
                label="Message"
                fullWidth
                multiline
                rows={3}
                sx={{ mb: 2 }}
                error={!!errors.body}
                helperText={errors.body?.message}
              />
            )}
          />
          <Button
            type="submit"
            variant="contained"
            endIcon={creating ? <CircularProgress size={16} /> : <SendIcon />}
            disabled={creating}
          >
            Post
          </Button>
        </form>
      </Paper>

      <Divider sx={{ mb: 3 }} />

      {/* Posts List */}
      {posts.length === 0 ? (
        <Typography color="text.secondary" textAlign="center">
          No messages yet. Be the first to post!
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {posts.map((post) => (
            <Card key={post.id} sx={{ borderLeft: post.pinned ? '4px solid' : 'none', borderColor: 'secondary.main' }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                  <Box>
                    <Typography variant="subtitle1" fontWeight={500}>
                      {post.authorName}
                      {post.pinned && (
                        <Chip icon={<PinIcon />} label="Pinned" size="small" color="secondary" sx={{ ml: 1 }} />
                      )}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatDateTime(post.createdAt)}
                    </Typography>
                  </Box>
                  <Box>
                    <IconButton size="small" onClick={() => handleTogglePin(post)} title={post.pinned ? 'Unpin' : 'Pin'}>
                      <PinIcon color={post.pinned ? 'secondary' : 'action'} />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDelete(post.id)} color="error" title="Delete">
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Box>
                <Typography sx={{ whiteSpace: 'pre-wrap' }}>{post.body}</Typography>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}
    </Box>
  );
}

export default MessageBoardPage;

